/**
 * Hook for getting extracted requirements data from backend
 */

import { useQuery } from '@apollo/client';

import type { SvbrgFormStepType } from '@/generated/gql/graphql';

import { GET_EXTRACTED_REQUIREMENTS } from './extractedRequirements.gql';

interface UseExtractedRequirementsOptions {
  chatSessionId: string;
  stepType: SvbrgFormStepType;
  enabled?: boolean;
}

/**
 * Hook to get extracted requirements data for a specific form step
 */
export const useExtractedRequirements = ({
  chatSessionId,
  stepType,
  enabled = true,
}: UseExtractedRequirementsOptions) => {
  const { data, loading, error } = useQuery(GET_EXTRACTED_REQUIREMENTS, {
    variables: {
      input: {
        chatSessionId,
        stepType,
      },
    },
    skip: !enabled,
    errorPolicy: 'all', // Don't crash on errors, just return empty data
  });

  const extractedRequirements = data?.sevenbridges?.getExtractedRequirements;

  return {
    hasData: extractedRequirements?.hasData ?? false,
    data: extractedRequirements?.data ?? null,
    loading,
    error,
  };
};

/**
 * Check if requirements data should be used for pre-population
 */
export const shouldPrePopulateFromRequirements = (hasData: boolean, existingData?: any): boolean => {
  // Don't pre-populate if user has already entered data
  if (existingData && Object.keys(existingData).length > 0) {
    return false;
  }

  // Only pre-populate if we have extracted data
  return hasData;
};
